import * as stylex from '@stylexjs/stylex'
import { useCallback, useEffect, useState } from 'react'

import Avatar from './Avatar'
import Button from './Button'
import { FormMessage, Input } from './FormControls'
import PaymentProgress from './PaymentProgress'
import {
  findPerson,
  formatMoney,
  v1API,
  type Expense,
  type ExpenseGuestShareOverview,
  type GuestShareParticipant,
  type Overview,
} from '../lib/ledger'

const styles = stylex.create({
  section: { display: 'flex', flexDirection: 'column', rowGap: '12px' },
  heading: { display: 'flex', alignItems: 'start', justifyContent: 'space-between', gap: '12px' },
  title: { fontSize: '14px', fontWeight: 650, color: '#3d463f' },
  copy: { marginTop: '3px', maxWidth: '34rem', fontSize: '12px', lineHeight: '1.15rem', color: '#748077' },
  list: { display: 'flex', flexDirection: 'column', rowGap: '10px' },
  card: { display: 'flex', flexDirection: 'column', rowGap: '12px', borderWidth: 1, borderStyle: 'solid', borderColor: '#e2e9df', borderRadius: '0.9rem', backgroundColor: '#fbfdfb', padding: '14px' },
  personRow: { display: 'flex', alignItems: 'center', gap: '10px' },
  personText: { minWidth: 0, flex: '1 1 0%' },
  name: { fontSize: '13px', fontWeight: 650, color: '#35443a' },
  amount: { marginTop: '2px', fontSize: '11px', color: '#738078' },
  actions: { display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px' },
  linkState: { fontSize: '11px', color: '#6f7c72' },
  generated: { display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) auto', gap: '8px' },
  requests: { display: 'flex', flexDirection: 'column', rowGap: '8px', borderTopWidth: 1, borderColor: '#e6ece4', paddingTop: '12px' },
  request: { display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px', borderRadius: '0.75rem', backgroundColor: '#fff8e8', padding: '10px 12px' },
  requestMain: { minWidth: '150px', flex: '1 1 0%' },
  requestTitle: { fontSize: '12px', fontWeight: 650, color: '#6f5720' },
  requestMeta: { marginTop: '2px', fontSize: '11px', lineHeight: '1rem', color: '#87703c' },
  empty: { borderRadius: '0.75rem', backgroundColor: '#f4f7f3', padding: '10px 12px', fontSize: '11px', color: '#718075' },
})

type Props = {
  data: Overview
  expense: Expense
  onSettlementConfirmed: () => Promise<void>
}

function expiresLabel(value?: string) {
  if (!value) return 'No expiry'
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? 'Expiry unavailable' : `Expires ${date.toLocaleDateString()}`
}

export default function ExpenseGuestSharing({ data, expense, onSettlementConfirmed }: Props) {
  const [overview, setOverview] = useState<ExpenseGuestShareOverview | null>(null)
  const [generatedLinks, setGeneratedLinks] = useState<Record<string, string>>({})
  const [busy, setBusy] = useState('')
  const [copied, setCopied] = useState('')
  const [error, setError] = useState('')

  const load = useCallback(async () => {
    try {
      setError('')
      setOverview(await v1API.getExpenseGuestShares(expense.id))
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Could not load payment links.')
    }
  }, [expense.id])

  useEffect(() => {
    let cancelled = false
    v1API.getExpenseGuestShares(expense.id)
      .then((result) => {
        if (!cancelled) setOverview(result)
      })
      .catch((reason) => {
        if (!cancelled) setError(reason instanceof Error ? reason.message : 'Could not load payment links.')
      })
    return () => {
      cancelled = true
    }
  }, [expense.id])

  async function createLink(participant: GuestShareParticipant) {
    const action = `link:${participant.personId}`
    if (busy) return
    setBusy(action)
    setError('')
    try {
      const created = await v1API.createExpenseGuestShare(expense.id, participant.personId)
      const url = `${window.location.origin}/share/${created.token}`
      setGeneratedLinks((current) => ({ ...current, [participant.personId]: url }))
      await load()
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Could not create the payment link.')
    } finally {
      setBusy('')
    }
  }

  async function copyLink(personID: string) {
    const url = generatedLinks[personID]
    if (!url) return
    try {
      await navigator.clipboard.writeText(url)
      setCopied(personID)
      window.setTimeout(() => setCopied(''), 1600)
    } catch {
      setError('Could not copy automatically. Select and copy the link manually.')
    }
  }

  async function revokeLink(participant: GuestShareParticipant) {
    if (!participant.activeLink || busy || !window.confirm('Disable this payment link? Pending requests will stay available for review.')) return
    setBusy(`revoke:${participant.personId}`)
    setError('')
    try {
      await v1API.revokeGuestShareLink(participant.activeLink.id)
      setGeneratedLinks((current) => {
        const next = { ...current }
        delete next[participant.personId]
        return next
      })
      await load()
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Could not disable the payment link.')
    } finally {
      setBusy('')
    }
  }

  async function confirmRequest(requestID: string, amount: string) {
    if (busy || !window.confirm(`Confirm that you received ${amount}? This will update the real balance.`)) return
    setBusy(`confirm:${requestID}`)
    setError('')
    try {
      await v1API.confirmGuestSettlementRequest(requestID)
      await onSettlementConfirmed()
      await load()
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Could not confirm the settlement request.')
    } finally {
      setBusy('')
    }
  }

  async function rejectRequest(requestID: string) {
    if (busy) return
    const reason = window.prompt('Optional reason for rejecting this request:', 'Payment not received')
    if (reason === null) return
    setBusy(`reject:${requestID}`)
    setError('')
    try {
      await v1API.rejectGuestSettlementRequest(requestID, reason)
      await load()
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Could not reject the settlement request.')
    } finally {
      setBusy('')
    }
  }

  if (overview && overview.participants.length === 0 && !error) return null

  return <section {...stylex.props(styles.section)}>
    <div {...stylex.props(styles.heading)}>
      <div>
        <h3 {...stylex.props(styles.title)}>Guest payment links</h3>
        <p {...stylex.props(styles.copy)}>Send someone their part of this expense without requiring a SettleUp account. Payment requests stay pending until you confirm receiving the money.</p>
      </div>
    </div>
    {error && <FormMessage tone="error">{error}</FormMessage>}
    {!overview && !error && <p {...stylex.props(styles.empty)}>Loading payment-link options…</p>}
    {overview && <div {...stylex.props(styles.list)}>{overview.participants.map((participant) => {
      const person = findPerson(data, participant.personId)
      if (!person) return null
      const pending = participant.requests.filter((request) => request.status === 'pending')
      const generatedURL = generatedLinks[participant.personId]
      const linkBusy = busy === `link:${participant.personId}` || busy === `revoke:${participant.personId}`
      return <div {...stylex.props(styles.card)} key={participant.personId}>
        <div {...stylex.props(styles.personRow)}>
          <Avatar person={person} size={36} />
          <div {...stylex.props(styles.personText)}>
            <p {...stylex.props(styles.name)}>{person.name}</p>
            <p {...stylex.props(styles.amount)}>Share {formatMoney(participant.originalAmountCents, participant.currency)} · {formatMoney(participant.remainingAmountCents, participant.currency)} remaining</p>
          </div>
        </div>
        <PaymentProgress confirmedAmountCents={participant.confirmedAmountCents} currency={participant.currency} originalAmountCents={participant.originalAmountCents} pendingAmountCents={participant.pendingAmountCents} remainingAmountCents={participant.remainingAmountCents} />
        <div {...stylex.props(styles.actions)}>
          <Button disabled={linkBusy || participant.remainingAmountCents <= 0} onClick={() => void createLink(participant)} size="sm" variant={participant.activeLink ? 'secondary' : 'primary'}>{linkBusy ? 'Working…' : participant.activeLink ? 'Regenerate link' : 'Create payment link'}</Button>
          {participant.activeLink && <Button disabled={linkBusy} onClick={() => void revokeLink(participant)} size="sm" variant="ghost">Disable</Button>}
          {participant.activeLink && <span {...stylex.props(styles.linkState)}>Active · {expiresLabel(participant.activeLink.expiresAt)}</span>}
        </div>
        {generatedURL && <div {...stylex.props(styles.generated)}>
          <Input aria-label={`Payment link for ${person.name}`} readOnly value={generatedURL} />
          <Button onClick={() => void copyLink(participant.personId)} variant="secondary">{copied === participant.personId ? 'Copied' : 'Copy'}</Button>
        </div>}
        {generatedURL && <FormMessage tone="info">For security, the full link is shown only when it is created or regenerated. Save or send it now.</FormMessage>}
        {pending.length > 0 && <div {...stylex.props(styles.requests)}>{pending.map((request) => {
          const amount = formatMoney(request.amountCents, request.currency)
          return <div {...stylex.props(styles.request)} key={request.id}>
            <div {...stylex.props(styles.requestMain)}>
              <p {...stylex.props(styles.requestTitle)}>{person.name} says they paid {amount}</p>
              <p {...stylex.props(styles.requestMeta)}>{request.note ? `${request.note} · ` : ''}${new Date(request.requestedAt).toLocaleString()}</p>
            </div>
            <Button disabled={Boolean(busy)} onClick={() => void rejectRequest(request.id)} size="sm" variant="ghost">Reject</Button>
            <Button disabled={Boolean(busy)} onClick={() => void confirmRequest(request.id, amount)} size="sm">Confirm</Button>
          </div>
        })}</div>}
      </div>
    })}</div>}
  </section>
}
